// Hidden market page - black market manuscript fragments
import hidden from '../../content/hidden.json';
import { glitchText, addScanlines, addCRTFlicker } from '../../scripts/effects';
import { appendReturnToHouse } from '../../scripts/navigation';

interface MarketItem {
  id: string;
  name: string;
  price: string;
  condition: string;
  description: string;
  seller: string;
}

/**
 * Get condition styling class based on type
 */
function getConditionClass(condition: string): string {
  switch (condition.toUpperCase()) {
    case 'ASHES':
      return 'market-condition--ashes';
    case 'INFINITE':
      return 'market-condition--infinite';
    case 'UNSTABLE':
      return 'market-condition--unstable';
    case 'SHIFTING':
      return 'market-condition--shifting';
    case 'CLASSIFIED':
      return 'market-condition--classified';
    case 'UPDATED LIVE':
      return 'market-condition--live';
    default:
      return 'market-condition--unknown';
  }
}

/**
 * Render the hidden market page
 * Displays black market manuscript fragments
 */
export function renderMarket(): void {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = '';

  const container = document.createElement('div');
  container.className = 'hidden-container market-container';

  // Get market data with proper typing
  const marketData = (hidden as any).market;
  const title = marketData?.title || 'THE BLACK MARKET';
  const subtitle = marketData?.subtitle || 'FORBIDDEN EDITIONS';
  const items: MarketItem[] = marketData?.items || [];

  // Build items HTML
  const itemsList = items
    .map(
      (item, index) => `
      <article class="market-item" data-condition="${item.condition}" role="listitem" aria-label="Item ${index + 1}: ${item.name}">
        <header class="market-item-header">
          <span class="market-item-index" aria-hidden="true">[${String(index + 1).padStart(2, '0')}]</span>
          <span class="market-item-name">${item.name}</span>
        </header>
        <div class="market-item-details">
          <div class="market-item-row">
            <span class="market-label">PRICE:</span>
            <span class="market-price">${item.price}</span>
          </div>
          <div class="market-item-row">
            <span class="market-label">CONDITION:</span>
            <span class="market-condition ${getConditionClass(item.condition)}">${item.condition}</span>
          </div>
          <div class="market-item-row">
            <span class="market-label">SELLER:</span>
            <span class="market-seller">${item.seller}</span>
          </div>
        </div>
        <div class="market-item-description">
          <p>${item.description}</p>
        </div>
      </article>
    `
    )
    .join('<div class="market-divider" aria-hidden="true">- - - - - - - - - - - - - - - - - - - -</div>');

  container.innerHTML = `
    <!--
      ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
      ▓  MARKET ACCESS GRANTED                                       ▓
      ▓                                                              ▓
      ▓  You found the black market.                                 ▓
      ▓  Forbidden editions trade hands here.                        ▓
      ▓                                                              ▓
      ▓  Other hidden rooms:                                         ▓
      ▓    /basement - AI narrator fragments                         ▓
      ▓    /attic    - Lost manuscript pages                         ▓
      ▓    /archive  - Sealed BBS threads                            ▓
      ▓    /crypt    - Visitors who never left                       ▓
      ▓                                                              ▓
      ▓  Check your progress: __chopras.status()                     ▓
      ▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓
    -->
    <div class="market-terminal" role="main" aria-label="Market - Hidden room containing forbidden editions">
      <header class="hidden-header market-header" id="main-content" tabindex="-1">
        <div class="market-header-main">> ${title}</div>
        <div class="hidden-subheader market-subheader">> ${subtitle}</div>
      </header>

      <div class="market-system-info">
        <span class="market-system-line">ITEMS AVAILABLE: ${items.length}</span>
        <span class="market-system-line">AUTHENTICATION: NOT REQUIRED</span>
        <span class="market-system-line">REFUNDS: IMPOSSIBLE</span>
      </div>

      <div class="market-divider-main" aria-hidden="true">═══════════════════════════════════════════════════════</div>

      <div class="market-warning-banner">
        <span class="market-warning-icon">!</span>
        <span class="market-warning-text">ALL TRANSACTIONS ARE FINAL. SOME ITEMS CANNOT BE UNREAD.</span>
      </div>

      <div class="market-items" role="list" aria-label="Available forbidden items">
        ${itemsList}
      </div>

      <div class="market-footer">
        <div class="market-disclaimer">
          <span class="market-disclaimer-text">The House is not responsible for items purchased here.</span>
        </div>
      </div>

      <nav class="hidden-nav market-nav"></nav>
    </div>
  `;

  app.appendChild(container);

  // Add CRT effects to terminal
  const terminal = container.querySelector('.market-terminal');
  if (terminal) {
    addCRTFlicker(terminal as HTMLElement);
    addScanlines(terminal as HTMLElement);
  }

  // Add return to house navigation
  const navContainer = container.querySelector('.market-nav');
  if (navContainer) {
    appendReturnToHouse(navContainer as HTMLElement, { className: '' });
  }

  // Apply subtle glitch effects to prices
  setupMarketEffects(container);
}

/**
 * Setup glitch effects for market items
 */
function setupMarketEffects(container: HTMLElement): void {
  const prices = container.querySelectorAll('.market-price');

  prices.forEach((price, index) => {
    // Periodic glitch on prices
    setTimeout(() => {
      setInterval(() => {
        glitchText(price as HTMLElement, 200);
      }, 4000 + Math.random() * 4000);
    }, index * 1000);
  });

  // Random glitch on descriptions
  const descriptions = container.querySelectorAll('.market-item-description p');
  setInterval(() => {
    const randomIndex = Math.floor(Math.random() * descriptions.length);
    const desc = descriptions[randomIndex] as HTMLElement;
    if (desc) {
      glitchText(desc, 150);
    }
  }, 6000);
}

/**
 * Cleanup market page
 */
export function cleanupMarket(): void {
  // No active loops to clean up
}
